/**
 * Fitting the AI's portions to the calorie target. The AI adds up its own calories and tends to overshoot (5–12% on
 * the plans of 26/10/2026, 20–40% before), which works against a weight-loss plan. Foods weighed in grams or ml
 * ("150g", "200 ml") are scaled together until the day lands on the target; counted ones ("2 unidades (100g)",
 * "1 scoop (30g)") stay as written. Every food keeps its macros in proportion to its new weight.
 */

const WEIGHED = /^(\d+(?:[.,]\d+)?)(\s*)(g|ml)$/i;
const TOLERANCE = 0.03;
// Past these the AI's day was too far off to fix by weighing: it stays close to what the AI meant
const MIN_FACTOR = 0.5;
const MAX_FACTOR = 1.6;

/** The amount of a food weighed in grams or ml, or null for a counted one. */
export const weighedAmount = (quantity) => {
  const match = String(quantity ?? '').trim().match(WEIGHED);
  return match ? { amount: parseFloat(match[1].replace(',', '.')), space: match[2], unit: match[3] } : null;
};

/** Portions a kitchen scale reads: 5 g steps, 10 g from 100 g. */
export const roundAmount = (amount) => (amount >= 100 ? Math.round(amount / 10) * 10 : Math.max(5, Math.round(amount / 5) * 5));
export const oneDecimal = (value) => Math.round(value * 10) / 10;

/**
 * The amount a food can be re-weighed by: its weighed quantity, or the grams an imported food carries beside a
 * household measure ("2 fatias (50 g)"). Null for a counted food with no weight.
 */
export const amountOf = (food) => {
  const weighed = weighedAmount(food?.quantity);
  if (weighed) return weighed;
  return food?.grams > 0 ? { amount: food.grams, space: ' ', unit: food.unit || 'g' } : null;
};

/**
 * A weighed food at exactly `amount` grams or ml, written the way its quantity was ("150g" stays without the space),
 * its calories and macros following. A counted food, or an amount that isn't a weight, comes back as it was. An
 * imported food re-weighed loses its household measure: "2 fatias (50 g)" at 80 g reads "80 g".
 */
export const foodAtAmount = (food, amount) => {
  const weighed = amountOf(food);
  if (!weighed || weighed.amount <= 0 || !(amount > 0)) return food;
  const ratio = amount / weighed.amount;
  const changed = {
    ...food,
    quantity: `${amount}${weighed.space}${weighed.unit}`,
    calories: Math.round((food.calories || 0) * ratio),
    protein: oneDecimal((food.protein || 0) * ratio),
    carbs: oneDecimal((food.carbs || 0) * ratio),
    fat: oneDecimal((food.fat || 0) * ratio),
  };
  if (food.fiber != null) changed.fiber = oneDecimal(food.fiber * ratio);
  if (food.grams != null) {
    changed.grams = amount;
    changed.amountText = null;
    changed.assumed = false;
  }
  return changed;
};

/** The food at `factor` times its weight (rounded to the scale), its calories and macros following. */
export const scaleFood = (food, factor) => {
  const weighed = weighedAmount(food.quantity);
  if (!weighed || weighed.amount <= 0) return food;
  return foodAtAmount(food, roundAmount(weighed.amount * factor));
};

const caloriesOf = (foods) => foods.reduce((total, food) => total + (food.calories || 0), 0);

/**
 * The meals with their weighed foods scaled so the foods add up to `target` kcal (within 3%). The free meal has no
 * foods and is left alone; its budget is already outside the target given here.
 */
export const fitMealsToTarget = (meals, target) => {
  const foods = meals.filter((meal) => !meal.isFreeMeal).flatMap((meal) => meal.foods || []);
  const total = caloriesOf(foods);
  if (!target || !total || Math.abs(total - target) / target <= TOLERANCE) return meals;

  const weighedCalories = caloriesOf(foods.filter((food) => weighedAmount(food.quantity)));
  if (!weighedCalories) return meals;
  const factor = Math.min(Math.max((target - (total - weighedCalories)) / weighedCalories, MIN_FACTOR), MAX_FACTOR);

  return meals.map((meal) => (meal.isFreeMeal ? meal : { ...meal, foods: (meal.foods || []).map((food) => scaleFood(food, factor)) }));
};
